import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '@/domains/auth/services/auth.service';
import { getApiErrorMessage } from '@/utils/apiErrors';

const INPUT_CLASSES =
  'w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all';

export default function ResetPasswordForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [login, setLogin] = useState(params.get('login') ?? '');
  const [token, setToken] = useState(params.get('token') ?? '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const isValid =
    login.trim().length > 0 &&
    token.trim().length > 0 &&
    password.length >= 8 &&
    password === passwordConfirmation;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await resetPassword({
        login: login.trim(),
        token: token.trim(),
        password,
        password_confirmation: passwordConfirmation,
      });
      setDone(true);
      setTimeout(() => navigate('/login?reset=1'), 2000);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Impossible de réinitialiser le mot de passe.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col antialiased">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm px-4 h-16 flex items-center">
        <div className="max-w-6xl w-full mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-teal-500 rounded-xl flex items-center justify-center text-slate-950 font-black text-lg shadow-md shadow-teal-500/20">
              O
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-base tracking-tight text-slate-900 leading-none">ONAPILA</span>
              <span className="text-[9px] text-teal-600 font-medium tracking-widest uppercase mt-0.5">Livrer easy</span>
            </div>
          </Link>
          <Link to="/login" className="text-xs font-semibold text-teal-600 hover:underline">
            Connexion
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-md p-6 sm:p-8">
            <div className="mb-6">
              <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
                <i className="ri-shield-keyhole-line text-xl" />
              </div>
              <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Nouveau mot de passe</h1>
              <p className="text-sm text-slate-500 mt-1.5">
                Choisissez un mot de passe sécurisé (8 caractères minimum).
              </p>
            </div>

            {done ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-800 text-center">
                <i className="ri-checkbox-circle-line text-2xl block mb-2" />
                Mot de passe mis à jour. Redirection vers la connexion…
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {!params.get('login') && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                      E-mail ou téléphone
                    </label>
                    <input
                      value={login}
                      onChange={(e) => setLogin(e.target.value)}
                      className={INPUT_CLASSES}
                      placeholder="vous@exemple.com ou +224…"
                      disabled={loading}
                    />
                  </div>
                )}

                {!params.get('token') && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                      Code reçu
                    </label>
                    <input
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      className={INPUT_CLASSES}
                      placeholder="Code à 6 chiffres ou jeton"
                      disabled={loading}
                    />
                  </div>
                )}

                {params.get('login') && (
                  <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 truncate">
                    Compte : <span className="font-semibold text-slate-700">{login}</span>
                  </p>
                )}

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${INPUT_CLASSES} pr-11`}
                      autoComplete="new-password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Confirmer le mot de passe
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    className={INPUT_CLASSES}
                    autoComplete="new-password"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="rounded-xl bg-rose-50 border border-rose-100 px-3 py-2 text-xs text-rose-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !isValid}
                  className="w-full py-3 bg-teal-500 text-slate-950 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-teal-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> Enregistrement…
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line" /> Enregistrer
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
